import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { Check, X, Folder, File as FileIcon, Container, Compass, Rocket } from "lucide-react";
import { deployCloneList, deployJobStatus, deployRunYaml } from "../../redux/actionCreator";
import "./deployWizard.scss";

const STEPS = ["Git", "Location", "Pick YAML", "Options", "Review"];

/**
 * Step-by-step deploy wizard.
 *   1. Git      — repo URL, branch, credentials
 *   2. Location — clone directory + target server
 *   3. Pick YAML — clone/pull the repo, then choose the compose/manifest file
 *                  and how to run it (docker compose | kubectl apply)
 *   4. Options  — env vars + action (up/down)
 *   5. Review   — confirm, run, watch the per-server log
 *
 * Single-server per run (the file list is discovered on the chosen server,
 * then the same server runs it). `initial` prefills git/location from the
 * project's saved deploy config.
 */
export default function DeployWizard({ show, onHide, projectId, appId, servers = [], initial = {}, onDeployed }) {
  const dispatch = useDispatch();
  const [step, setStep] = useState(0);

  // Step 1 — git
  const [gitUrl, setGitUrl] = useState(initial.git_url || "");
  const [branch, setBranch] = useState(initial.git_branch || "main");
  const [gitUser, setGitUser] = useState(initial.git_username || "");
  const [gitPass, setGitPass] = useState(""); // empty = use saved
  // Step 2 — location + server
  const [repoDir, setRepoDir] = useState(initial.repo_dir || "");
  const [serverId, setServerId] = useState(servers[0]?._id ? String(servers[0]._id) : "");
  // Step 3 — file + mode
  const [scanning, setScanning] = useState(false);
  const [scanErr, setScanErr] = useState(null);
  const [files, setFiles] = useState([]);
  const [dirs, setDirs] = useState([]); // folders containing yaml
  const [dockerfiles, setDockerfiles] = useState([]);
  const [sha, setSha] = useState("");
  const [file, setFile] = useState("");
  const [isFolder, setIsFolder] = useState(false);
  const [mode, setMode] = useState("compose"); // compose | kubectl
  // Step 4 — options
  // Seeded from the application's own env vars. Every other field here
  // prefills from `initial`; leaving these empty meant the variables an
  // operator had already entered on the app never reached compose, and
  // compose then failed on a missing .env.
  const [envVars, setEnvVars] = useState(
    (initial.env_vars || []).map((e) => ({ key: e?.key || "", value: e?.value ?? "" })),
  ); // [{key,value}]
  const [action, setAction] = useState("up"); // up | down
  const [buildImage, setBuildImage] = useState(false);
  const [imageTag, setImageTag] = useState("");
  const [buildContext, setBuildContext] = useState(".");
  const [rolloutRestart, setRolloutRestart] = useState(true);
  // Compose: rebuild images from the freshly-pulled source. On by default —
  // without it `up -d` reuses the old image and the deploy silently does
  // nothing visible.
  const [composeBuild, setComposeBuild] = useState(true);
  const [composePull, setComposePull] = useState(false);
  // Step 5 — run
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);
  // Live progress. `liveLog` grows as the server streams; `elapsed` is shown
  // because a compose build can run for minutes and a still spinner is
  // indistinguishable from a hung one.
  const [liveLog, setLiveLog] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const logRef = useRef(null);
  const pollRef = useRef(null);
  const stopRef = useRef(false);

  // Stick to the bottom as output arrives, the way a terminal does.
  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [liveLog]);

  // Stop polling if the modal is closed mid-deploy. The run itself carries
  // on server-side — it is a job, not a request — so reopening and polling
  // the same id would pick it up again.
  useEffect(() => () => { stopRef.current = true; clearInterval(pollRef.current); }, []);

  const server = useMemo(
    () => servers.find((s) => String(s._id) === String(serverId)) || null,
    [servers, serverId]
  );

  const gitValid = /^(https?:\/\/|git@).+/.test(gitUrl.trim());
  const locValid = repoDir.trim().startsWith("/") && !!serverId;

  const reset = () => {
    setStep(0); setFiles([]); setFile(""); setSha(""); setScanErr(null);
    setResults(null); setAction("up"); setMode("compose");
    // Back to the app's own variables, not empty — clearing them here was
    // throwing away the seeded values if the wizard stayed mounted.
    setEnvVars((initial.env_vars || []).map((e) => ({ key: e?.key || "", value: e?.value ?? "" })));
    setLiveLog(""); setElapsed(0);
    stopRef.current = true;
    clearInterval(pollRef.current);
  };
  // Closing mid-run is fine: the deploy is a server-side job, not this
  // request, so it carries on. Only the blocking clone/scan holds us.
  const close = () => { if (scanning) return; reset(); onHide && onHide(); };

  // Step 2 → 3: clone/pull on the chosen server then list YAML files.
  const scan = async () => {
    setScanning(true);
    setScanErr(null);
    setFiles([]);
    const res = await dispatch(
      deployCloneList(projectId, {
        app_id: appId,
        server_id: serverId,
        git_url: gitUrl.trim(),
        git_branch: branch.trim(),
        git_username: gitUser.trim() || undefined,
        git_password: gitPass || undefined,
        repo_dir: repoDir.trim(),
      })
    );
    setScanning(false);
    if (res?.status || Array.isArray(res?.data?.files)) {
      const fl = res.data?.files || [];
      setFiles(fl);
      setDirs(res.data?.dirs || []);
      setDockerfiles(res.data?.dockerfiles || []);
      setSha(res.data?.sha || "");
      // Default selection + mode from the first file's heuristic.
      const first = fl[0];
      if (first) {
        setFile(first.path);
        setIsFolder(false);
        setMode(first.likely_compose ? "compose" : "kubectl");
      }
      // Prefill an image tag from the repo name if there's a Dockerfile.
      if ((res.data?.dockerfiles || []).length) {
        const repoName = (repoDir.trim().split("/").pop() || "app").toLowerCase();
        setImageTag(`${repoName}:latest`);
      }
      setStep(2);
      if (!res?.status && res?.message) setScanErr(res.message);
    } else {
      setScanErr(res?.message || "Clone / scan failed");
      setStep(2);
    }
  };

  const run = async () => {
    setRunning(true);
    setResults(null);
    setLiveLog("");
    setElapsed(0);
    stopRef.current = false;
    const startedAt = Date.now();
    const tick = setInterval(() => setElapsed(Math.floor((Date.now() - startedAt) / 1000)), 1000);

    const res = await dispatch(
      deployRunYaml(projectId, {
        async: true,
        app_id: appId,
        server_ids: [serverId],
        repo_dir: repoDir.trim(),
        file,
        is_folder: isFolder,
        mode,
        action,
        env_vars: envVars.filter((e) => e.key),
        build_image: mode === "kubectl" && buildImage,
        image_tag: imageTag.trim(),
        build_context: buildContext.trim() || ".",
        rollout_restart: mode === "kubectl" && action === "up" && rolloutRestart,
        compose_build: mode === "compose" && action === "up" ? composeBuild : false,
        compose_pull: mode === "compose" && action === "up" ? composePull : false,
      })
    );
    const jobId = res?.data?.job_id;
    if (!res?.status || !jobId) {
      clearInterval(tick);
      setRunning(false);
      setResults(res || { status: false, message: "Could not start the deploy" });
      return;
    }

    // Poll for new output. Only the delta is sent each time, keyed on how
    // much we already hold.
    let offset = 0;
    const poll = async () => {
      if (stopRef.current) return;
      const p = await dispatch(deployJobStatus(projectId, jobId, offset));
      if (!p?.status) {
        // A single failed poll is usually a blip; keep going and let the
        // user close out if it never recovers.
        return;
      }
      if (p.data.chunk) {
        offset = p.data.offset;
        setLiveLog((prev) => prev + p.data.chunk);
      }
      if (p.data.done) {
        clearInterval(pollRef.current);
        clearInterval(tick);
        setRunning(false);
        const final = {
          status: p.data.job_status === "success",
          message: p.data.message,
          data: { results: p.data.results || [] },
        };
        setResults(final);
        if (final.status) onDeployed && onDeployed();
      }
    };
    pollRef.current = setInterval(poll, 1500);
    poll();
  };

  const next = () => {
    if (step === 1) { scan(); return; }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const canNext =
    (step === 0 && gitValid) ||
    (step === 1 && locValid) ||
    (step === 2 && !!file) ||
    step === 3;

  const setEnv = (i, k, v) =>
    setEnvVars((arr) => arr.map((e, idx) => (idx === i ? { ...e, [k]: v } : e)));

  return (
    <Modal show={show} onHide={close} size="lg" centered backdrop="static" className="deploy-wizard">
      <Modal.Header closeButton={!running && !scanning}>
        <h4 className="m-0">Deploy wizard</h4>
      </Modal.Header>

      <div className="wiz-steps">
        {STEPS.map((s, i) => (
          <div key={s} className={`wiz-step ${i === step ? "active" : ""} ${i < step ? "done" : ""}`}>
            <span className="wiz-step-num">{i < step ? <Check size={12} /> : i + 1}</span>
            <span className="wiz-step-label">{s}</span>
          </div>
        ))}
      </div>

      <Modal.Body>
        {/* Step 1 — Git */}
        {step === 0 && (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Git repository URL *</Form.Label>
              <Form.Control value={gitUrl} onChange={(e) => setGitUrl(e.target.value)}
                placeholder="https://gitlab.com/acme/app.git"
                style={{ fontFamily: "ui-monospace, monospace" }} />
            </Form.Group>
            <div className="row g-3">
              <div className="col-md-4">
                <Form.Label>Branch</Form.Label>
                <Form.Control value={branch} onChange={(e) => setBranch(e.target.value)} placeholder="main" />
              </div>
              <div className="col-md-4">
                <Form.Label>Username <small className="text-muted">(private repos)</small></Form.Label>
                <Form.Control value={gitUser} onChange={(e) => setGitUser(e.target.value)} placeholder="optional" />
              </div>
              <div className="col-md-4">
                <Form.Label>Token / password</Form.Label>
                <Form.Control type="password" value={gitPass} onChange={(e) => setGitPass(e.target.value)}
                  placeholder={initial.has_git_password ? "•••• (saved)" : "optional"} />
              </div>
            </div>
            <small className="text-muted d-block mt-2">
              Leave the token blank to reuse the project's saved credential. SSH-style URLs (git@…) auth via the server's key.
            </small>
          </>
        )}

        {/* Step 2 — Location + server */}
        {step === 1 && (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Clone location on the server *</Form.Label>
              <Form.Control value={repoDir} onChange={(e) => setRepoDir(e.target.value)}
                placeholder="/opt/apps/myapp" style={{ fontFamily: "ui-monospace, monospace" }} />
              <small className="text-muted">Absolute path. The repo is cloned (or pulled if it already exists) here.</small>
            </Form.Group>
            <Form.Group>
              <Form.Label>Target server *</Form.Label>
              <Form.Select value={serverId} onChange={(e) => setServerId(e.target.value)}>
                <option value="">— Select server —</option>
                {servers.map((s) => (
                  <option key={s._id} value={s._id}>{s.label} ({s.username}@{s.host})</option>
                ))}
              </Form.Select>
            </Form.Group>
          </>
        )}

        {/* Step 3 — Pick YAML + run mode */}
        {step === 2 && (
          <>
            {scanErr && <div className="alert alert-warning">{scanErr}</div>}
            {sha && <div className="text-muted small mb-2">Checked out <code>{sha}</code> on <strong>{server?.label}</strong></div>}
            {files.length === 0 ? (
              <div className="alert alert-secondary">
                No <code>.yml</code> / <code>.yaml</code> files found in the repo. Go back and check the branch, or add a compose/manifest file to the repo.
              </div>
            ) : (
              <>
                <Form.Label>Pick what to run *</Form.Label>
                <div className="wiz-file-list">
                  {/* Folders first — apply a whole manifest set at once. */}
                  {dirs.map((d) => (
                    <label key={`dir:${d.path}`} className={`wiz-file ${isFolder && file === d.path ? "selected" : ""}`}>
                      <input type="radio" name="yamlfile" checked={isFolder && file === d.path}
                        onChange={() => { setFile(d.path); setIsFolder(true); setMode("kubectl"); }} />
                      <span className="wiz-file-name d-inline-flex align-items-center gap-1"><Folder size={12} /> {d.path === "." ? "(repo root)" : d.path}/</span>
                      <span className="wiz-badge folder">folder · {d.count}</span>
                    </label>
                  ))}
                  {files.map((f) => (
                    <label key={f.path} className={`wiz-file ${!isFolder && file === f.path ? "selected" : ""}`}>
                      <input type="radio" name="yamlfile" checked={!isFolder && file === f.path}
                        onChange={() => { setFile(f.path); setIsFolder(false); setMode(f.likely_compose ? "compose" : "kubectl"); }} />
                      <span className="wiz-file-name d-inline-flex align-items-center gap-1"><FileIcon size={12} /> {f.path}</span>
                      {f.likely_compose && <span className="wiz-badge">compose</span>}
                      <span className="wiz-file-size">{f.size} B</span>
                    </label>
                  ))}
                </div>
                <Form.Label className="mt-3">Run it with</Form.Label>
                <div className="wiz-mode-toggle">
                  <button type="button" className={mode === "compose" ? "on" : ""} disabled={isFolder} onClick={() => setMode("compose")}>
                    <Container size={14} className="me-1" /> docker compose
                  </button>
                  <button type="button" className={mode === "kubectl" ? "on" : ""} onClick={() => setMode("kubectl")}>
                    <Compass size={14} className="me-1" /> kubectl apply
                  </button>
                </div>
                {isFolder && <small className="text-muted d-block mt-1">Folder selected — applies every manifest in it via <code>kubectl apply -f</code>.</small>}
              </>
            )}
          </>
        )}

        {/* Step 4 — Options */}
        {step === 3 && (
          <>
            <Form.Label>Action</Form.Label>
            <div className="wiz-mode-toggle mb-3">
              <button type="button" className={action === "up" ? "on" : ""} onClick={() => setAction("up")}>
                {mode === "compose" ? "up -d (start)" : "apply (create/update)"}
              </button>
              <button type="button" className={action === "down" ? "on" : ""} onClick={() => setAction("down")}>
                {mode === "compose" ? "down (stop)" : "delete"}
              </button>
            </div>
            {mode === "compose" && action === "up" && (
              <div className="wiz-build-box mb-3">
                <Form.Check
                  type="switch"
                  id="compose-build-toggle"
                  checked={composeBuild}
                  onChange={(e) => setComposeBuild(e.target.checked)}
                  label="Build images on the server from the pulled source"
                />
                <small className="text-muted d-block">
                  Adds <code>--build</code>. Without it <code>up -d</code> reuses the
                  image already on the server, so a deploy after a code change brings
                  the <strong>old</strong> build back up and looks like it worked.
                  Turn off only to restart with the existing images.
                </small>
                {composeBuild && (
                  <Form.Check
                    type="switch"
                    id="compose-pull-toggle"
                    className="mt-2"
                    checked={composePull}
                    onChange={(e) => setComposePull(e.target.checked)}
                    label={<>Also refresh base images (<code>--pull always</code>)</>}
                  />
                )}
              </div>
            )}
            {mode === "kubectl" && action === "up" && (
              <div className="wiz-build-box mb-3">
                <Form.Check
                  type="switch"
                  id="build-toggle"
                  checked={buildImage}
                  onChange={(e) => setBuildImage(e.target.checked)}
                  label="Build the image & import into the cluster (k3s/containerd)"
                />
                <small className="text-muted d-block">
                  Needed when your manifest uses a local image (e.g. <code>imagePullPolicy: IfNotPresent</code>) and there's no registry.
                  Runs <code>docker build</code> then imports into containerd before applying.
                </small>
                {buildImage && (
                  <div className="row g-2 mt-1">
                    <div className="col-md-7">
                      <Form.Label className="small mb-1">Image tag (must match the manifest)</Form.Label>
                      <Form.Control size="sm" value={imageTag} onChange={(e) => setImageTag(e.target.value)}
                        placeholder="nodejs-app:latest" style={{ fontFamily: "ui-monospace, monospace" }} />
                    </div>
                    <div className="col-md-5">
                      <Form.Label className="small mb-1">Build context</Form.Label>
                      <Form.Control size="sm" value={buildContext} onChange={(e) => setBuildContext(e.target.value)}
                        placeholder="." style={{ fontFamily: "ui-monospace, monospace" }} />
                    </div>
                    {dockerfiles.length > 0 && (
                      <div className="col-12"><small className="text-muted">Dockerfile(s): {dockerfiles.join(", ")}</small></div>
                    )}
                  </div>
                )}
              </div>
            )}
            {mode === "kubectl" && action === "up" && (
              <div className="mb-3">
                <Form.Check
                  type="switch"
                  id="rollout-toggle"
                  checked={rolloutRestart}
                  onChange={(e) => setRolloutRestart(e.target.checked)}
                  label="Restart deployments after apply"
                />
                <small className="text-muted d-block">
                  Runs <code>kubectl rollout restart</code> on each applied deployment so pods recreate and pick up a freshly-built image (an <code>apply</code> that's "unchanged" otherwise leaves old pods running).
                </small>
              </div>
            )}
            <Form.Label>Environment variables <small className="text-muted">(optional)</small></Form.Label>
            {mode === "compose" && (
              <div className="small text-muted mb-2">
                Written to <code>.env</code> next to the compose file (mode 600) and
                also exported for the run, so both <code>env_file:</code> and
                <code className="ms-1">{"$"}{"{VAR}"}</code> interpolation resolve. Regenerated
                every deploy — <code>.env</code> is gitignored and <code>git clean</code> removes it.
              </div>
            )}
            {envVars.map((e, i) => (
              <div key={i} className="d-flex gap-2 mb-2">
                <Form.Control size="sm" placeholder="KEY" value={e.key} onChange={(ev) => setEnv(i, "key", ev.target.value)} />
                <Form.Control size="sm" placeholder="value" value={e.value} onChange={(ev) => setEnv(i, "value", ev.target.value)} />
                <Button size="sm" variant="outline-danger" aria-label="Remove env var" onClick={() => setEnvVars((arr) => arr.filter((_, idx) => idx !== i))}><X size={14} /></Button>
              </div>
            ))}
            <Button size="sm" variant="light" onClick={() => setEnvVars((arr) => [...arr, { key: "", value: "" }])}>+ Add variable</Button>
            <small className="text-muted d-block mt-2">
              For compose these are passed to the command (interpolation + container env). For kubectl they prefix the command.
            </small>
          </>
        )}

        {/* Step 5 — Review + run */}
        {step === 4 && (
          <>
            {running || (liveLog && !results) ? (
              /* Live view: the whole point of the job/polling change —
                 previously this was a blank modal for minutes, then a
                 timeout error with no log at all. */
              <div>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <Spinner size="sm" animation="border" />
                  <strong>
                    {mode === "compose" ? "docker compose" : "kubectl"} {action === "down" ? "down" : "up"} running…
                  </strong>
                  <span className="text-muted small ms-auto" style={{ fontFamily: "ui-monospace, monospace" }}>
                    {Math.floor(elapsed / 60)}m {String(elapsed % 60).padStart(2, "0")}s
                  </span>
                </div>
                <pre className="wiz-log" ref={logRef} style={{ maxHeight: 340, overflow: "auto" }}>
                  {liveLog || "Starting…"}
                </pre>
                <div className="small text-muted mt-1">
                  This runs on the server, not in your browser — closing the
                  wizard won't stop it.
                </div>
              </div>
            ) : !results ? (
              <div className="wiz-review">
                <Row k="Server" v={`${server?.label} (${server?.username}@${server?.host})`} />
                <Row k="Repo" v={`${gitUrl} @ ${branch}`} />
                <Row k="Location" v={repoDir} />
                <Row k={isFolder ? "Folder" : "File"} v={isFolder ? `${file}/ (all manifests)` : file} />
                <Row k="Run with" v={mode === "compose" ? `docker compose ${action === "down" ? "down" : "up -d"}` : `kubectl ${action === "down" ? "delete" : "apply"}`} />
                {mode === "kubectl" && buildImage && action === "up" && (
                  <Row k="Build image" v={`${imageTag} (context ${buildContext}) → import to cluster`} />
                )}
                {envVars.filter((e) => e.key).length > 0 && (
                  <Row k="Env" v={envVars.filter((e) => e.key).map((e) => e.key).join(", ")} />
                )}
              </div>
            ) : (
              <div className={`alert ${results.status ? "alert-success" : "alert-danger"}`}>
                <strong className="d-inline-flex align-items-center gap-2">
                  {results.status ? <Check size={14} /> : <X size={14} />} {results.message}
                </strong>
                {(results.data?.results || []).map((r) => (
                  <pre key={r.server_id} className="wiz-log">{`# ${r.label || r.server_id} — ${r.status}\n${r.log || r.message || ""}`}</pre>
                ))}
                {/* The async path returns a summary without per-server logs,
                    so fall back to the streamed output rather than showing
                    a bare headline. */}
                {!(results.data?.results || []).some((r) => r.log) && liveLog && (
                  <pre className="wiz-log" style={{ maxHeight: 340, overflow: "auto" }}>{liveLog}</pre>
                )}
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between">
        <Button variant="light" onClick={back} disabled={step === 0 || running || scanning || !!results}>
          ‹ Back
        </Button>
        <div className="d-flex gap-2">
          <Button variant="light" onClick={close} disabled={scanning}>Close</Button>
          {step < 4 ? (
            <Button variant="primary" onClick={next} disabled={!canNext || scanning}>
              {scanning ? <><Spinner size="sm" animation="border" /> Cloning…</> : (step === 1 ? "Clone & scan ›" : "Next ›")}
            </Button>
          ) : !results ? (
            <Button variant="success" onClick={run} disabled={running}>
              {running ? <><Spinner size="sm" animation="border" /> Deploying…</> : <><Rocket size={14} className="me-1" /> Deploy</>}
            </Button>
          ) : (
            <Button variant="primary" onClick={close}>Done</Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
}

function Row({ k, v }) {
  return (
    <div className="wiz-review-row">
      <span className="wiz-review-k">{k}</span>
      <span className="wiz-review-v">{v}</span>
    </div>
  );
}
